/* eslint-disable react/prop-types */
import { clsx } from 'clsx';
import { getJxtInputClass } from './styles';
import { IJxtInput } from './types';
import { ForwardedRef, forwardRef, useMemo } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Message } from 'react-hook-form';

const JxtInput = (
  {
    name,
    type = 'text',
    value,
    placeholder,
    label,
    helperText,
    disabled,
    readonly,
    showErrorMessages = true,
    leftIcon,
    rightAddon,
    multiLines,
    validationSchema,
    register,
    formState,
    getFieldState,
    onChange,
    errorStyle = false,
    className = '',
    ...rest
  }: IJxtInput,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref: ForwardedRef<HTMLElement>
) => {
  // @TODO : see when react-hook-form properly supports react-compiler
  // https://github.com/orgs/react-hook-form/discussions/12524
  // https://jooxter.atlassian.net/browse/ND-6018
  'use no memo';
  const { invalid, isTouched, error, isDirty } = getFieldState(name, formState);
  const hasRightAddon = !!rightAddon && !multiLines;
  const hasErrors = useMemo(() => isTouched && Object.keys(error ?? {}).length > 0, [error, isTouched]);
  const classes = useMemo(
    () =>
      getJxtInputClass({
        leftIcon,
        hasRightAddon,
        hasErrors: hasErrors || errorStyle,
        multiLines,
        readonly,
      }),
    [hasErrors, leftIcon, hasRightAddon, errorStyle, multiLines, readonly]
  );

  return (
    <div className={clsx(className, 'w-full')}>
      {label && <label className="block pb-1 text-neutral-120 text-title-m font-medium">{label}</label>}
      <div className="relative">
        {multiLines ? (
          <textarea
            className={classes}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={hasErrors}
            {...register(name, validationSchema)}
            readOnly={readonly}
          />
        ) : (
          <>
            {leftIcon && (
              <div
                className={clsx(
                  disabled ? 'text-neutral-60' : 'text-neutral-120',
                  'pointer-events-none absolute inset-y-0 pl-2.5 flex items-center'
                )}
                aria-hidden="true"
              >
                <i className={`fas fa-${leftIcon}`} />
              </div>
            )}
            <input
              // Spread first: explicit props and react-hook-form bindings below must win over `as` wrappers'.
              {...rest}
              className={classes}
              type={type}
              defaultValue={value}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readonly}
              aria-invalid={hasErrors}
              autoComplete="off"
              {...register(name, { ...validationSchema, onChange: onChange })}
            />
            {hasRightAddon && <div className="absolute inset-y-0 right-0 flex items-center pr-2.5">{rightAddon}</div>}
          </>
        )}
      </div>
      {helperText && !hasErrors && (
        <p
          className={clsx(
            isDirty && isTouched && !invalid ? 'text-green-80' : 'text-neutral-80',
            'text-body-xs',
            'pt-1'
          )}
        >
          {helperText}
          {isDirty && isTouched && !invalid && <i className="fas ml-1 fa-check text-green-80 fa-s" />}
        </p>
      )}
      {showErrorMessages && error && (
        <ErrorMessage
          name={name}
          errors={{ [name]: error }}
          render={({ message }: { message?: Message }) => <p className="text-red-100 pt-1">{message}</p>}
        />
      )}
    </div>
  );
};

export default forwardRef(JxtInput);
